import { isValidElement } from 'react';
import { GridComparatorFn } from '@mui/x-data-grid';

// ==============================|| GLOBAL TABLE - CLIENT-SIDE SORTING ||============================== //
// Global-table cells are usually React elements (TruncatedText, Chip, Stack…) rather
// than raw values. For client-side sorting we extract a comparable value using the
// project's conventions: `text` (TableText / TruncatedText), `label` (Chip), `title`,
// otherwise we recurse into `children` down to the first string / number.

const comparableOf = (value: unknown): string | number | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'string') return value.trim();
	if (typeof value === 'number') return value;
	if (typeof value === 'boolean') return value ? 1 : 0;
	if (Array.isArray(value)) {
		for (let i = 0; i < value.length; i += 1) {
			const inner = comparableOf(value[i]);
			if (inner !== null) return inner;
		}
		return null;
	}
	if (isValidElement(value)) {
		const props = value.props as Record<string, unknown>;
		return comparableOf(props.text ?? props.label ?? props.title ?? props.children);
	}
	return null;
};

// Text extracted from a cell value, for the overflow tooltip (see cell-tooltip.tsx):
// the same conventions as the comparator — `text`, `label`, `title`, then `children`.
export const cellTextOf = (value: unknown): string | null => {
	const comparable = comparableOf(value);
	if (comparable === null) return null;
	const text = String(comparable).trim();
	return text.length > 0 ? text : null;
};

// Comparator for client-side sortable columns: numbers compare numerically, everything
// else via localeCompare (ro, numeric) — cells with no extractable value sort first.
export const reactNodeComparator: GridComparatorFn = (v1, v2) => {
	const a = comparableOf(v1);
	const b = comparableOf(v2);
	if (a === null && b === null) return 0;
	if (a === null) return -1;
	if (b === null) return 1;
	if (typeof a === 'number' && typeof b === 'number') return a - b;
	return String(a).localeCompare(String(b), 'ro', { numeric: true, sensitivity: 'base' });
};
